import React, { useState, useEffect } from 'react';
import { MediaAsset } from '../../../models/media';
import { MediaManager } from '../MediaManager/MediaManager';
import { useApi } from '../../../hooks/useApi';
import styles from './MediaSelector.module.css';

interface MediaSelectorProps {
  value?: string | null;
  onChange: (mediaId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  error?: string;
  preloadedMedia?: MediaAsset | null; // Добавляем возможность передать готовый объект
}

export const MediaSelector: React.FC<MediaSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Выберите изображение',
  disabled = false,
  required = false,
  label,
  error,
  preloadedMedia,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | null>(null);
  const api = useApi();

  // Загружаем данные медиа при изменении value или preloadedMedia
  useEffect(() => {
    if (preloadedMedia && value === preloadedMedia.id) {
      setSelectedMedia(preloadedMedia);
    } else if (value && !selectedMedia) {
      // В реальном приложении здесь будет запрос к API для получения медиа по ID
      // api.media.getMediaById(value).then(setSelectedMedia);
    } else if (!value) {
      setSelectedMedia(null);
    }
  }, [value, selectedMedia, api, preloadedMedia]);

  const handleSelect = (media: MediaAsset | null) => {
    onChange(media?.id || null);
    setSelectedMedia(media);
    setIsModalOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSelectedMedia(null);
  };

  const getImageUrl = (media: MediaAsset): string => {
    // Используем структуру из API ответа
    if (media.variants && 'sources' in media.variants) {
      const sources = media.variants.sources;
      if (sources.webp && sources.webp.length > 0) {
        // Ищем наиболее подходящий размер для превью (около 400px)
        const suitableSize =
          sources.webp.find(
            (s: { url: string; w: number; h: number }) => s.w >= 400,
          ) || sources.webp[0];
        return suitableSize.url;
      }
      // Fallback на original
      if (media.variants.meta?.original?.url) {
        return media.variants.meta.original.url;
      }
    }
    return media.original_url || '';
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div
        className={`${styles.selector} ${error ? styles.error : ''} ${
          disabled ? styles.disabled : ''
        }`}
      >
        {value ? (
          <div className={styles.selectedMedia}>
            <div className={styles.preview}>
              {selectedMedia ? (
                <img
                  src={getImageUrl(selectedMedia)}
                  alt="Выбранное изображение"
                  className={styles.previewImage}
                />
              ) : (
                <div className={styles.loadingPreview}>
                  <span>🖼️</span>
                  <span>Загрузка...</span>
                </div>
              )}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className={styles.changeButton}
                disabled={disabled}
              >
                Изменить
              </button>
              <button
                type="button"
                onClick={handleClear}
                className={styles.clearButton}
                disabled={disabled}
              >
                ✕
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={styles.selectButton}
            disabled={disabled}
          >
            <span className={styles.selectIcon}>📁</span>
            <span className={styles.selectText}>{placeholder}</span>
          </button>
        )}
      </div>

      {error && <div className={styles.errorText}>{error}</div>}

      {isModalOpen && (
        <MediaManager
          isOpen={true}
          onSelect={handleSelect}
          onClose={() => setIsModalOpen(false)}
          selectedMediaId={value || undefined}
        />
      )}
    </div>
  );
};
